import { CustomerIoAdapter, CustomerIoPushMetric } from "../CustomerIoAdapter/CustomerIoAdapter";
import { IAnalyticsAdapter } from "../IAnalyticsAdapter";
import { SegmentAdapter } from "../SegmentAdapter/SegmentAdapter";
import { AnalyticsOptions, AnalyticsProperties, AnalyticsTraits } from "../types";

export class AnalyticsService {
  private adapter: IAnalyticsAdapter;
  private customerIo: CustomerIoAdapter;
  private rememberedPushDevice?: Readonly<{ userId: string; deviceToken: string }>;

  constructor(adapter: IAnalyticsAdapter, customerIo: CustomerIoAdapter) {
    this.adapter = adapter;
    this.customerIo = customerIo;
  }

  track(event: string, properties: AnalyticsProperties, options?: AnalyticsOptions): void {
    this.adapter.track(event, properties, options);
  }

  page(name: string, properties?: AnalyticsProperties): void {
    this.adapter.page(name, properties);
    this.customerIo.page(name, properties);
  }

  identify(userId: string, traits: AnalyticsTraits): void {
    this.adapter.identify(userId, traits);
    this.customerIo.identify(userId, traits);
  }

  rememberPushDevice(userId: string, deviceToken: string): void {
    this.rememberedPushDevice = { userId, deviceToken };
  }

  async reset(): Promise<void> {
    try {
      if (this.rememberedPushDevice) {
        await this.customerIo.deletePushDevice(
          this.rememberedPushDevice.userId,
          this.rememberedPushDevice.deviceToken,
        );
      }
    } catch {
      console.error("[RMD]: failed to delete Customer.io push device during analytics reset");
    } finally {
      this.rememberedPushDevice = undefined;
      this.adapter.reset();
      this.customerIo.reset();
    }
  }

  /**
   * Reports a push metric to Customer.io against its own delivery. Separate from {@link track}: that
   * sends an analytics event, this moves Customer.io's delivery metric, which is what broadcast
   * workflow conditions read.
   */
  reportPushMetric(
    metric: CustomerIoPushMetric,
    deliveryId?: string,
    deliveryToken?: string,
  ): Promise<void> {
    return this.customerIo.reportPushMetric(metric, deliveryId, deliveryToken);
  }
}

// Initialize Segment first so we can pass its track method to Customer.io
const segmentAdapter = new SegmentAdapter(import.meta.env.REACT_APP_SEGMENT_WRITE_KEY);

const Analytics = new AnalyticsService(
  segmentAdapter,
  new CustomerIoAdapter(
    import.meta.env.REACT_APP_CUSTOMERIO_WRITE_KEY,
    import.meta.env.REACT_APP_CUSTOMERIO_SITE_ID,
    {
      // Pass Segment's track method so Customer.io in-app events get tracked
      segmentTrack: (event, properties) => segmentAdapter.track(event, properties),
    },
  ),
);

export default Analytics;
